import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, TestTube, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';

interface WebhookEvent {
    value: string;
    label: string;
}

interface Webhook {
    id: number;
    name: string;
    url: string;
    type: string;
    events: string[];
    is_active: boolean;
    failure_count: number;
    last_triggered_at: string | null;
    logs_count: number;
}

export default function WebhooksPage() {
    const [webhooks, setWebhooks] = useState<Webhook[]>([]);
    const [events, setEvents] = useState<WebhookEvent[]>([]);
    const [showForm, setShowForm] = useState(false);
    const [expandedLogs, setExpandedLogs] = useState<number | null>(null);
    const [logs, setLogs] = useState<Record<number, any[]>>({});
    const [form, setForm] = useState({
        name: '',
        url: '',
        type: 'discord',
        events: [] as string[],
    });

    const fetchWebhooks = () => {
        api.get<{ data: Webhook[] }>('/webhooks').then((r) => setWebhooks(r.data));
    };

    useEffect(() => {
        fetchWebhooks();
        api.get<{ data: WebhookEvent[] }>('/webhook-events').then((r) => setEvents(r.data));
    }, []);

    const handleCreate = async () => {
        await api.post('/webhooks', form);
        setForm({ name: '', url: '', type: 'discord', events: [] });
        setShowForm(false);
        fetchWebhooks();
    };

    const handleDelete = async (id: number) => {
        await api.delete(`/webhooks/${id}`);
        fetchWebhooks();
    };

    const handleTest = async (id: number) => {
        await api.post(`/webhooks/${id}/test`);
    };

    const handleReset = async (id: number) => {
        await api.post(`/webhooks/${id}/reset`);
        fetchWebhooks();
    };

    const toggleEvent = (event: string) => {
        setForm((f) => ({
            ...f,
            events: f.events.includes(event)
                ? f.events.filter((e) => e !== event)
                : [...f.events, event],
        }));
    };

    const toggleLogs = async (webhookId: number) => {
        if (expandedLogs === webhookId) {
            setExpandedLogs(null);
            return;
        }
        if (!logs[webhookId]) {
            const res = await api.get<{ data: any[] }>(`/webhooks/${webhookId}/logs`);
            setLogs((l) => ({ ...l, [webhookId]: res.data }));
        }
        setExpandedLogs(webhookId);
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-semibold">Webhooks</h1>
                <Button size="sm" onClick={() => setShowForm(!showForm)}>
                    <Plus className="mr-1 h-4 w-4" />
                    Add Webhook
                </Button>
            </div>

            {showForm && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">New Webhook</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <label className="mb-1 block text-sm font-medium">Name</label>
                                <Input
                                    value={form.name}
                                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                    placeholder="My Discord Webhook"
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-medium">Type</label>
                                <select
                                    value={form.type}
                                    onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                                    className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100"
                                >
                                    <option value="discord">Discord</option>
                                    <option value="slack">Slack</option>
                                    <option value="custom">Custom URL</option>
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium">Webhook URL</label>
                            <Input
                                value={form.url}
                                onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                                placeholder="https://discord.com/api/webhooks/..."
                            />
                        </div>
                        <div>
                            <label className="mb-2 block text-sm font-medium">Events</label>
                            <div className="flex flex-wrap gap-2">
                                {events.map((evt) => (
                                    <button
                                        key={evt.value}
                                        type="button"
                                        onClick={() => toggleEvent(evt.value)}
                                        className={`rounded-full px-3 py-1 text-xs transition ${
                                            form.events.includes(evt.value)
                                                ? 'bg-violet-600 text-white'
                                                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                                        }`}
                                    >
                                        {evt.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Button onClick={handleCreate} disabled={!form.name || !form.url || form.events.length === 0}>
                                Create
                            </Button>
                            <Button variant="outline" onClick={() => setShowForm(false)}>
                                Cancel
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {webhooks.length === 0 && !showForm && (
                <Card>
                    <CardContent className="py-8 text-center text-sm text-slate-500">
                        No webhooks configured. Add one to get notified when events happen.
                    </CardContent>
                </Card>
            )}

            {webhooks.map((wh) => (
                <Card key={wh.id}>
                    <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <h3 className="font-medium">{wh.name}</h3>
                                    <Badge variant={wh.is_active ? 'default' : 'secondary'} className="text-xs">
                                        {wh.type}
                                    </Badge>
                                    {wh.failure_count >= 5 && (
                                        <Badge variant="destructive" className="text-xs">
                                            {wh.failure_count} failures
                                        </Badge>
                                    )}
                                </div>
                                <p className="mt-1 truncate text-xs text-slate-500">{wh.url}</p>
                                <div className="mt-2 flex flex-wrap gap-1">
                                    {wh.events.map((e) => (
                                        <span key={e} className="rounded bg-slate-800 px-2 py-0.5 text-xs text-slate-400">
                                            {e}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <div className="flex gap-1">
                                <Button variant="ghost" size="sm" onClick={() => handleTest(wh.id)} title="Send test">
                                    <TestTube className="h-4 w-4" />
                                </Button>
                                {wh.failure_count > 0 && (
                                    <Button variant="ghost" size="sm" onClick={() => handleReset(wh.id)} title="Reset failures">
                                        <RotateCcw className="h-4 w-4" />
                                    </Button>
                                )}
                                <Button variant="ghost" size="sm" onClick={() => toggleLogs(wh.id)} title="View logs">
                                    {expandedLogs === wh.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(wh.id)} className="text-red-400 hover:text-red-300">
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>

                        {expandedLogs === wh.id && logs[wh.id] && (
                            <div className="mt-3 border-t border-slate-800 pt-3">
                                <p className="mb-2 text-xs font-medium text-slate-400">Recent Logs</p>
                                {logs[wh.id].length === 0 ? (
                                    <p className="text-xs text-slate-500">No logs yet.</p>
                                ) : (
                                    <div className="space-y-1">
                                        {logs[wh.id].slice(0, 10).map((log: any) => (
                                            <div key={log.id} className="flex items-center gap-2 text-xs">
                                                <span className={log.success ? 'text-green-400' : 'text-red-400'}>
                                                    {log.success ? 'OK' : 'FAIL'}
                                                </span>
                                                <span className="text-slate-500">{log.event}</span>
                                                <span className="text-slate-600">
                                                    {log.response_code && `(${log.response_code})`}
                                                </span>
                                                <span className="ml-auto text-slate-600">
                                                    {new Date(log.created_at).toLocaleString()}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>
            ))}
        </div>
    );
}
